import { FlexyForm, FormObject, FormStructure } from '@europrocurement/flexy-form';
import { Box } from '@mui/material';
import * as React from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import {
    GroupementGroupementRead,
    GroupementJsonldGroupementCreate,
    GroupementJsonldGroupementRead,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconName } from '@fortawesome/sharp-regular-svg-icons';
import { domainSelector, useOffreService, useSociete } from '@europrocurement/l2d-domain';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { maximumLengthRule } from '@europrocurement/l2d-modules/validation';
import { formatPayload } from '../forms/format';
import { ModelForm } from '../forms/types';
import FontAwesomeDocLink from '../components/FontAwesomeDocLink';

type OfferGroupFormObject = {
    id?: number;
    libelle: string;
    domaine: number;
    rang?: string | number | null;
    iconeName?: string | null;
};

/** Convertit la valeur brute du champ rang (string issue de l'input) en nombre ou undefined. */
const normalizeRang = (value: unknown): number | undefined =>
    value === '' || value === undefined || value === null ? undefined : Number(value);

const Form: React.FunctionComponent<ModelForm<GroupementGroupementRead>> = function (props) {
    const { offerGroupModel: model } = useOffreService();

    const { afterSubmit, entity = {}, isCreate = false, onLabelChange } = props;
    const { libelle: label, iconeName, rang } = entity as GroupementJsonldGroupementRead;
    const initialLabel = isCreate ? '' : (label ?? '');
    const initialIcone = isCreate ? '' : (iconeName ?? '');
    let initialRang: string;
    if (isCreate) {
        initialRang = rang != null ? String(rang) : '';
    } else {
        initialRang = '';
    }

    const [stateLabel, setLabel] = React.useState({
        initialLabel,
        value: initialLabel,
    });
    const [stateIcone, setIcone] = React.useState({
        initialIcone,
        value: initialIcone,
    });
    const [stateRang, setRang] = React.useState({
        initialRang,
        value: initialRang,
    });

    const successCallback = () => {
        afterSubmit();
    };

    // Prefetched options
    const domainsOptions = useSelector(domainSelector).main.data;

    // FormContext
    const formContext = useForm<FieldValues, unknown, undefined>({
        defaultValues: isCreate ? {} : entity,
        mode: 'onTouched',
    });

    const companyId = useSociete();

    const { request } = useApiRequest();

    const formatOutputSchema = {
        domaine: (domaineOption: { id: string }) => domaineOption.id,
        // Rang optionnel : chaîne vide => non envoyé, sinon converti en nombre.
        rang: normalizeRang,
    };

    // FormStructure
    const OfferGroupFormStructure: FormStructure[] = [
        {
            isDisplayable: isCreate, // No domain edition allowed
            type: 'select',
            name: 'domaine',
            inputlabel: 'Domaine',
            options: domainsOptions,
            getOptionLabel: (option: { libelle: string }) => option.libelle,
            getOptionValue: (option: { id: number }) => option.id.toString(),
            rules: {
                required: "La sélection d'un domaine est obligatoire",
            },
            xs: 12,
            placeholder: 'Sélectionnez un domaine',
        },
        {
            type: 'text',
            name: 'libelle',
            inputlabel: 'Libellé',
            rules: {
                required: 'Le libellé est obligatoire',
                validate: {
                    ...maximumLengthRule(255),
                },
            },
            onChangeInput: (event: React.FormEvent<HTMLInputElement>) => {
                const { value } = event.currentTarget;
                setLabel({
                    ...stateLabel,
                    value,
                });
                // Remonte le libellé courant vers la fiche pour l'afficher dans l'en-tête.
                onLabelChange?.(value);
            },
            xs: 12,
            placeholder: 'Libellé',
        },
        {
            type: 'text',
            name: 'iconeName',
            inputlabel: 'Icône (https://fontawesome.com/search)',
            placeholder: 'Icône',
            xs: 12,
            sm: 12,
            md: 9,
            lg: 9,
            onChangeInput: (event: React.FormEvent<HTMLInputElement>) => {
                const { value } = event.currentTarget;
                setIcone({
                    ...stateIcone,
                    value,
                });
            },
        },
        {
            type: 'customItem',
            name: 'renderIcone',
            xs: 12,
            sm: 12,
            md: 3,
            lg: 3,
            renderField: () => {
                let iconeRender: string | null = formContext.getValues('iconeName');
                if (!iconeRender) {
                    iconeRender = 'image-slash';
                }
                return (
                    <Box
                        sx={{
                            fontSize: '40px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '35px',
                        }}
                    >
                        <FaOptionIcon icon={['fad', iconeRender as IconName]} />
                    </Box>
                );
            },
        },
        {
            type: 'customItem',
            name: 'iconsLink',
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12,
            renderField: () => (
                <Box sx={{ marginTop: '20px' }}>
                    <FontAwesomeDocLink />
                </Box>
            ),
        },
        {
            type: 'number',
            name: 'rang',
            inputlabel: "Rang (ordre d'affichage)",
            placeholder: 'Rang',
            xs: 12,
            onChangeInput: (event: React.FormEvent<HTMLInputElement>) => {
                const { value } = event.currentTarget;
                setRang({
                    ...stateRang,
                    value,
                });
            },
        },
    ];

    const createOfferGroup: SubmitHandler<FormObject> = async (
        formValues: OfferGroupFormObject,
    ) => {
        const offerGroup = formatPayload(formValues, formatOutputSchema);
        const createRequest = model.create({
            groupementJsonldGroupementCreate: offerGroup as GroupementJsonldGroupementCreate,
            xIdSociete: companyId,
        });
        await request(createRequest, {
            successCallback: (result) => afterSubmit(result.data?.id),
        });
    };

    const updateOfferGroup: SubmitHandler<FormObject> = async (
        offerGroup: OfferGroupFormObject,
    ) => {
        const { id: offerGroupId } = offerGroup;
        if (offerGroupId) {
            // Le domaine n'est pas éditable en update (peut être null) : on conserve les
            // valeurs brutes et on coerce uniquement `rang` (input number => string).
            const payload = {
                ...offerGroup,
                rang: normalizeRang(offerGroup.rang),
            };
            const updateRequest = model.update({
                id: offerGroupId.toString(),
                groupementJsonldGroupementUpdate: payload,
                xIdSociete: companyId,
            });
            await request(updateRequest, { successCallback });
        }
    };

    const dataHasChanged =
        stateLabel.value !== stateLabel.initialLabel ||
        stateIcone.value !== stateIcone.initialIcone ||
        stateRang.value !== stateRang.initialRang;

    return (
        <Box>
            <FlexyForm
                formContext={formContext}
                formObject={isCreate ? {} : (entity as FormObject)}
                formStructure={OfferGroupFormStructure}
                onSubmit={isCreate ? createOfferGroup : updateOfferGroup}
                isDisabled={!dataHasChanged}
            />
        </Box>
    );
};

export default Form;
